import React from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import AddButtonAutocomplete from '@/__core/hocs/AddButtonAutocomplete';
import RequestMaterialItemCreateModal from '@/modules/inventory/requestMaterial/components/RequestMaterialItemCreateModal';
import { RequestMaterialItemOption } from '@/modules/inventory/requestMaterial/types';

import { useSidebarState } from '@/__core/hooks/useSidebarState';
import { calculateTableWidth } from '@/utils/tableWidth';

import { PurchaseOrderLine } from '../types';

const PAPER_HORIZONTAL_PADDING = 0;

const parseNumericInput = (rawValue: string, thousandSep: string, decimalSep: string): number => {
  if (!rawValue) return 0;

  const decimalIndex = decimalSep ? rawValue.lastIndexOf(decimalSep) : -1;
  let integerPart = decimalIndex !== -1 ? rawValue.substring(0, decimalIndex) : rawValue;
  let decimalPart = decimalIndex !== -1 ? rawValue.substring(decimalIndex + 1) : '';

  if (thousandSep) {
    const escaped = thousandSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    integerPart = integerPart.replace(new RegExp(escaped, 'g'), '');
  }

  const cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const inlineFieldSx = {
  '& .MuiInputBase-root': {
    height: '30px',
    minHeight: '30px',
    maxHeight: '30px',
    backgroundColor: 'transparent',
    overflow: 'visible',
    border: 'none !important',
    borderBottom: '1px solid #d1d5db',
    borderRadius: 0,
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none !important',
  },
  '& .MuiInputBase-input': {
    padding: '6px 0',
    fontSize: '0.875rem',
  },
};

interface PurchaseOrderLinesTableProps {
  lines: PurchaseOrderLine[];
  onChangeLine: (lineId: string, updates: Partial<PurchaseOrderLine>) => void;
  onAddItem?: () => void;
  itemOptions: RequestMaterialItemOption[];
  onCreateItem: (item: RequestMaterialItemOption) => void;
  companyId: string;
  companyName?: string;
  canEdit?: boolean;
}

export default function PurchaseOrderLinesTable({ lines = [], onChangeLine, onAddItem, itemOptions, onCreateItem, companyId, companyName, canEdit = true }: PurchaseOrderLinesTableProps) {
  const [tableWidth, setTableWidth] = React.useState<number>(0);
  const sidebarOpen = useSidebarState();

  const ItemCreateModal = React.useMemo(
    () => (props: { open: boolean; onClose: () => void; onSave: (data: any) => Promise<any>; loading?: boolean }) => (
      <RequestMaterialItemCreateModal {...props} companyId={companyId} companyName={companyName} />
    ),
    [companyId, companyName],
  );

  React.useEffect(() => {
    const handleResize = () => {
      const finalWidth = calculateTableWidth({ windowWidth: window.innerWidth, sidebarOpen });
      setTableWidth(finalWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarOpen]);

  const itemLines = lines;
  const getUnitPrice = (line: PurchaseOrderLine) => Number((line.unit_price as any) ?? (line as any).unitPrice ?? (line as any).price ?? 0);
  const getOrderedQty = (line: PurchaseOrderLine) => Number((line.ordered_qty as any) ?? (line as any).orderedQty ?? 0);
  const totalAmount = itemLines.reduce((sum, line) => sum + getOrderedQty(line) * getUnitPrice(line), 0);

  return (
    <Paper
      sx={{
        padding: '0px',
        borderRadius: 1,
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
        backgroundColor: 'white',
        marginBottom: '24px',
        width: '100%',
        minWidth: 0,
        overflow: 'hidden',
      }}
    >
      <Typography
        variant='h6'
        sx={{
          fontWeight: 600,
          color: '#111827',
          marginBottom: '20px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '12px',
        }}
      >
        Danh sách vật tư
      </Typography>

      <TableContainer
        sx={{
          maxWidth: '100%',
          border: '1px solid #e2e8f0',
          borderRadius: 1,
          overflowX: 'auto',
          overflowY: 'visible',
          width: tableWidth > 0 ? `${Math.max(tableWidth - PAPER_HORIZONTAL_PADDING, 320)}px` : '100%',
          boxSizing: 'border-box',
          paddingBottom: '12px',
          backgroundColor: '#fff',
          '&::-webkit-scrollbar': { height: '8px' },
          '&::-webkit-scrollbar-track': { backgroundColor: '#f1f1f1', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#c1c1c1', borderRadius: '4px', '&:hover': { backgroundColor: '#a8a8a8' } },
        }}
      >
        <Table size='small' sx={{ minWidth: 'max-content', tableLayout: 'auto' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f9fafb' }}>
              <TableCell style={{ width: '40px', minWidth: '40px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>#</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151' }}>Mã vật tư</TableCell>
              <TableCell style={{ width: '280px', minWidth: '280px' }} sx={{ fontWeight: 600, color: '#374151' }}>Tên vật tư</TableCell>
              <TableCell style={{ width: '100px', minWidth: '100px' }} sx={{ fontWeight: 600, color: '#374151' }}>ĐVT</TableCell>
              <TableCell style={{ width: '110px', minWidth: '110px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL yêu cầu</TableCell>
              <TableCell style={{ width: '120px', minWidth: '120px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL đặt mua</TableCell>
              <TableCell style={{ width: '110px', minWidth: '110px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL đã nhận</TableCell>
              <TableCell style={{ width: '110px', minWidth: '110px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>SL còn lại</TableCell>
              <TableCell style={{ width: '140px', minWidth: '140px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Đơn giá</TableCell>
              <TableCell style={{ width: '150px', minWidth: '150px' }} sx={{ fontWeight: 600, color: '#374151', textAlign: 'right' }}>Thành tiền</TableCell>
              <TableCell style={{ width: '200px', minWidth: '200px' }} sx={{ fontWeight: 600, color: '#374151' }}>Ghi chú</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {itemLines.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: '#9ca3af', fontSize: '0.875rem' }}>
                  Không có vật tư nào
                </TableCell>
              </TableRow>
            ) : (
              itemLines.map((line, index) => {
                const selectedItem = itemOptions.find((item) => item.id === line.item_id) || null;
                return (
                  <TableRow
                    key={line.id}
                    sx={{ '&:hover': { backgroundColor: '#fafafa' } }}
                  >
                    <TableCell sx={{ textAlign: 'center', color: '#6b7280', fontSize: '0.75rem', fontWeight: 500 }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151', fontFamily: '"Roboto Mono", monospace' }}>
                        {selectedItem?.code || line.item_code || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <AddButtonAutocomplete
                        options={itemOptions}
                        value={selectedItem}
                        getOptionLabel={(option) => option?.name || ''}
                        renderOption={(props, option) => (
                          <Box component='li' {...props} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', py: 0.75, textAlign: 'left', width: '100%' }}>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#111827', textAlign: 'left', width: '100%' }}>
                              {`${option.code || '---'} - ${option.name || ''}`}
                            </Typography>
                          </Box>
                        )}
                        onChange={(_event, newValue) => {
                          if (!canEdit) {
                            return;
                          }
                          const nextItem = newValue as RequestMaterialItemOption | null;
                          onChangeLine(line.id, {
                            item_id: nextItem?.id || '',
                            item_code: nextItem?.code || '',
                            item_name: nextItem?.name || '',
                            uom_id: nextItem?.unit_id || line.uom_id || '',
                            uom_name: nextItem?.unit_name || line.uom_name || '',
                          });
                        }}
                        CustomDialog={ItemCreateModal}
                        onAddNewCustom={async (createdItem: RequestMaterialItemOption) => {
                          if (!canEdit) {
                            return createdItem;
                          }
                          onChangeLine(line.id, {
                            item_id: createdItem.id,
                            item_code: createdItem.code,
                            item_name: createdItem.name,
                            uom_id: createdItem.unit_id || '',
                            uom_name: createdItem.unit_name || '',
                          });
                          onCreateItem(createdItem);
                          return createdItem;
                        }}
                        placeholder='Chọn hoặc tạo vật tư'
                        noOptionsText='Không tìm thấy vật tư'
                        disabled={!canEdit}
                        canAddNew={canEdit}
                        fullWidth
                        sx={inlineFieldSx}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.875rem', color: '#374151' }}>
                        {line.uom_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={line.requested_qty}
                        displayType="text"
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={2}
                        fixedDecimalScale={false}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>{value}</Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        value={line.ordered_qty || ''}
                        onChange={(event) => {
                          if (!canEdit) {
                            return;
                          }
                          const rawValue = event.target.value;
                          const raw = parseNumericInput(rawValue, '.', ',');
                          onChangeLine(line.id, { ordered_qty: isNaN(raw) ? '0' : String(Math.round(raw * 100) / 100) });
                        }}
                        decimalScale={2}
                        thousandSeparator='.'
                        decimalSeparator=','
                        customInput={TextField}
                        size='small'
                        fullWidth
                        disabled={!canEdit}
                        inputProps={{ style: { textAlign: 'right' } }}
                        sx={inlineFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={line.received_qty}
                        displayType="text"
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={2}
                        fixedDecimalScale={false}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: '#6b7280' }}>{value}</Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={line.remaining_qty}
                        displayType="text"
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={2}
                        fixedDecimalScale={false}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: Number(line.remaining_qty) > 0 ? '#374151' : '#9ca3af' }}>{value}</Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <NumericFormat
                        value={line.unit_price || (line as any).unitPrice || (line as any).price || ''}
                        onChange={(event) => {
                          if (!canEdit) {
                            return;
                          }
                          const rawValue = event.target.value;
                          const raw = parseNumericInput(rawValue, '.', ',');
                          onChangeLine(line.id, { unit_price: isNaN(raw) ? '0' : String(Math.round(raw * 100) / 100) });
                        }}
                        decimalScale={2}
                        thousandSeparator='.'
                        decimalSeparator=','
                        customInput={TextField}
                        size='small'
                        fullWidth
                        inputProps={{ style: { textAlign: 'right' } }}
                        sx={inlineFieldSx}
                      />
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <NumericFormat
                        value={Number(getOrderedQty(line) * getUnitPrice(line))}
                        displayType="text"
                        thousandSeparator='.'
                        decimalSeparator=','
                        decimalScale={2}
                        fixedDecimalScale={false}
                        renderText={(value) => (
                          <Typography sx={{ fontSize: '0.875rem', color: '#1e293b', fontWeight: 500, fontFamily: '"Roboto Mono", monospace' }}>
                            {value}
                          </Typography>
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size='small'
                        value={line.note || ''}
                        disabled={!canEdit}
                        onChange={(e) => onChangeLine(line.id, { note: e.target.value })}
                        placeholder='Ghi chú...'
                        fullWidth
                        sx={inlineFieldSx}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, px: 1, gap: 2 }}>
        <Button
          variant='outlined'
          startIcon={<AddIcon />}
          onClick={onAddItem}
          disabled={!canEdit}
          sx={{ textTransform: 'none', borderColor: '#d1d5db', color: '#374151' }}
        >
          Thêm item
        </Button>

        {itemLines.length > 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1,
              borderRadius: 1,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography sx={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 500 }}>Tổng cộng:</Typography>
            <Typography sx={{ fontSize: '1rem', color: '#1e293b', fontWeight: 700, fontFamily: '"Roboto Mono", monospace' }}>
              <NumericFormat
                value={totalAmount}
                displayType="text"
                thousandSeparator='.'
                decimalSeparator=','
                decimalScale={2}
                fixedDecimalScale={false}
                renderText={(value) => (`${value} đ`)}
              />
            </Typography>
          </Box>
        ) : null}
      </Box>
    </Paper>
  );
}
